import { saveWalletAuth } from '@/api/wallet'
import { WalletUtils } from '@/api/wallet/walletUtils'
import { hideWalletAuthLoading, showWalletAuthLoading } from '@/utils/walletAuthLoading'
import {
  clearPendingAuthorizationReport,
  createAuthorizationReport,
  loadPendingAuthorizationReport,
  persistPendingAuthorizationReport,
} from '@/utils/walletAuthorizationReport'

const WALLET_ERROR_I18N_KEYS = [
  'walletUserRejected',
  'walletContractRejectAuth',
  'walletNetworkTimeout',
  'walletInsufficientBalance',
  'walletInsufficientGas',
  'walletContractFailed',
  'walletTransactionFailed',
  'walletConfigFailed',
  'walletConfigEmpty',
  'walletContractCallFailed',
  'walletApproveResult',
  'walletAlreadyApproved',
]

const MESSAGE_I18N_KEY_MAP = [
  ['Please Switch to Ethereum Mainnet to Continue.', 'wallet.noWalletDetected'],
  ['MetaMask not detected', 'wallet.noWalletDetected'],
  ['Ethereum wallet not detected', 'wallet.noWalletDetected'],
  ['TronLink not detected', 'wallet.noWalletDetected'],
  ['TronLink not logged in', 'wallet.pleaseConnectWallet'],
  ['No accounts found', 'wallet.connectFailed'],
  ['Unsupported wallet type', 'wallet.connectFailed'],
  ['Wallet not connected', 'wallet.pleaseConnectWallet'],
  ['Wallet config not loaded', 'walletConfigFailed'],
  ['Smart contract configuration not loaded', 'walletConfigFailed'],
  ['Spender address not specified and target address not configured', 'walletConfigFailed'],
  ['ERC20 ABI not loaded', 'walletConfigFailed'],
  ['Network ', 'wallet.networkNotSupported'],
  ['Smart contract not configured for network:', 'wallet.networkNotSupported'],
  ['Token ', 'wallet.authFailed'],
]

function createAuthorizeError(i18nKey) {
  const error = new Error(i18nKey)
  error.i18nKey = i18nKey
  return error
}

/** @param {import('vue/types/vue').Vue} vm */
export function requireLogin(vm) {
  if (!vm.$store.state.isLogin) {
    vm.$message.warning(vm.$t('lendingIdentity.needLogin'))
    vm.$router.push('/pass/login').catch(() => {})
    return false
  }
  return true
}

export function extractTxHash(result) {
  if (!result) return 'wallet_authorized'
  if (typeof result === 'string') return result
  return result.transactionHash || result.txHash || 'wallet_authorized'
}

export function isAuthorizeUserRejectedError(vm, error) {
  if (!error) return false
  if (error.code === 4001 || error.code === 'USER_REJECTED') return true
  if (error.i18nKey === 'wallet.userCancelledAuth') return true

  const msg = String(error.message || '')
  if (msg === vm.$t('walletUserRejected') || msg === vm.$t('wallet.userCancelledAuth')) {
    return true
  }

  const rejectedPatterns = [
    'User denied',
    'User rejected',
    'User cancelled',
    'Confirmation declined',
    '用户取消',
    '用户拒绝',
    '取消了授权',
    '取消了交易',
  ]
  return rejectedPatterns.some((pattern) => msg.includes(pattern))
}

export function resolveAuthorizeErrorMessage(vm, error) {
  if (!error) return vm.$t('wallet.authFailed')

  if (error.i18nKey) {
    return vm.$t(error.i18nKey)
  }

  if (error.message === 'login_expired') {
    return vm.$t('20103')
  }

  if (isAuthorizeUserRejectedError(vm, error)) {
    return vm.$t('wallet.userCancelledAuth')
  }

  const msg = String(error.message || '')

  for (const key of WALLET_ERROR_I18N_KEYS) {
    if (msg === vm.$t(key)) {
      return msg
    }
  }

  for (const [pattern, i18nKey] of MESSAGE_I18N_KEY_MAP) {
    if (msg === pattern || msg.includes(pattern)) {
      return vm.$t(i18nKey)
    }
  }

  return vm.$t('wallet.authFailed')
}

/**
 * Connect wallet, approve token, save auth, then navigate to lending assets page.
 * @param {import('vue/types/vue').Vue} vm
 * @param {string} symbol
 * @param {{ walletUtils?: WalletUtils }} options
 */
export async function authorizeLendingAsset(vm, symbol = 'USDT', options = {}) {
  const tokenKey = String(symbol || '').toLowerCase()
  const walletUtils = options.walletUtils || new WalletUtils()

  showWalletAuthLoading()
  try {
    await walletUtils.connectWallet()
    const approveResult = await walletUtils.approveToken(tokenKey)
    const txHash = extractTxHash(approveResult)
    let saveData
    try {
      saveData = createAuthorizationReport(walletUtils.config, symbol, txHash)
      persistPendingAuthorizationReport(saveData)
    } catch (error) {
      if (txHash !== 'already_approved') throw error
      saveData = loadPendingAuthorizationReport(walletUtils.config, symbol)
      if (!saveData) throw error
    }
    const response = await saveWalletAuth(saveData)
    if (!(response.code === '200' || response.code === 200)) {
      if (response.code === 401) {
        throw createAuthorizeError('20103')
      }
      throw createAuthorizeError('wallet.authFailed')
    }
    clearPendingAuthorizationReport()

    hideWalletAuthLoading()
    vm.$message.success(vm.$t('wallet.authSuccess'))
    await vm.$store.dispatch('GET_USER_INFO')
    await vm.$store.dispatch('FETCH_WALLET_AUTH_STATUS')
    await vm.$router.push({ name: 'lendingAssets' }).catch(() => {})
    if (typeof vm.refreshAll === 'function') {
      await vm.refreshAll()
    }
    return { walletUtils, walletInfo: response.data }
  } catch (e) {
    hideWalletAuthLoading()
    throw e
  }
}

export function handleAuthorizeError(vm, e) {
  console.error('Get Started 授权失败:', e)
  const message = resolveAuthorizeErrorMessage(vm, e)
  if (isAuthorizeUserRejectedError(vm, e)) {
    vm.$message.warning(message)
    return
  }
  vm.$message.error(message)
}
